import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import noop from 'lodash/noop';
import queryString from 'query-string';

import Browser from '@wetransfer/concorde-browser';

import { copyToClipboard } from '@wetransfer/concorde-clipboard';

import I18n from '@module/i18n';
import Analytics from '@module/analytics';

import Actions from '@app/store/action/';
import NotificationPositions from '@constant/notification-positions';

const BASE_CLASS_NAME = 'transfer-link';

class TransferLink extends React.PureComponent {
  urlNode = React.createRef();

  componentWillUnmount() {
    this.props.clearNotification();
  }

  copyUrlToClipboard = async (event) => {
    const { setNotification, onLinkCopied } = this.props;

    // PreventDefault to preserve selection
    if (event) {
      event.preventDefault();
    }

    Analytics.trackSnowplowEvent({
      category: 'transfer',
      action: 'click_copy_link',
    });

    const isCopied = await copyToClipboard(this.getShortURL())
      .then(() => true)
      .catch(() => false);

    if (this.urlNode.current) {
      this.urlNode.current.focus();
      this.urlNode.current.select();
    }

    if (isCopied) {
      setNotification(I18n.t('notification.link_copied'));
    } else {
      const shortcut = I18n.t(
        `shortcuts.${Browser.platform('Mac') ? 'cmd_c' : 'control_c'}`
      );
      setNotification(I18n.t('notification.copy_link_manual', { shortcut }));
    }

    onLinkCopied(isCopied);
  };

  getShortURL() {
    const { url, queryParams } = this.props;
    const {
      url: urlWithoutQuery,
      query: originalQueryParams,
    } = queryString.parseUrl(url);

    const newQueryParams = queryString.stringify({
      ...originalQueryParams,
      ...queryParams,
    });

    if (!newQueryParams) {
      return url;
    }

    return `${urlWithoutQuery}?${newQueryParams}`;
  }

  render() {
    const { label, noButton, buttonLabel, className, collapsed } = this.props;

    const cn = classnames(BASE_CLASS_NAME, className, {
      [`${BASE_CLASS_NAME}--collapsed`]: collapsed,
      [`${BASE_CLASS_NAME}--no-label`]: !label,
    });

    const url = this.getShortURL();

    return (
      <div className={cn}>
        {label && <div className={`${BASE_CLASS_NAME}__label`}>{label}</div>}
        <div className={`${BASE_CLASS_NAME}__url-button-wrapper`}>
          <input
            className={`${BASE_CLASS_NAME}__url`}
            ref={this.urlNode}
            readOnly
            onMouseDown={this.copyUrlToClipboard}
            value={url}
          />
          {!noButton && (
            <button
              onClick={this.copyUrlToClipboard}
              className={`${BASE_CLASS_NAME}__button`}
            >
              {buttonLabel}
            </button>
          )}
        </div>
      </div>
    );
  }
}

TransferLink.propTypes = {
  label: PropTypes.string,
  url: PropTypes.string.isRequired,
  noButton: PropTypes.bool,
  buttonLabel: PropTypes.string,
  className: PropTypes.string,
  collapsed: PropTypes.bool,
  queryParams: PropTypes.object,
  setNotification: PropTypes.func,
  notificationPosition: PropTypes.oneOf(Object.values(NotificationPositions))
    .isRequired,
  transferId: PropTypes.string,
  onLinkCopied: PropTypes.func,
  clearNotification: PropTypes.func,
};

TransferLink.defaultProps = {
  buttonLabel: I18n.t('transfer.buttons.copy_link'),
  setNotification: noop,
  onLinkCopied: noop,
  clearNotification: noop,
};

const mapStateToProps = ({ transfer: { shortURL: url, id: transferId } }) => ({
  url,
  transferId,
});

const mapDispatchToProps = (dispatch, ownProps) => {
  const { notificationPosition } = ownProps;

  return {
    setNotification: (value, ...rest) => {
      let action;
      switch (notificationPosition) {
        case NotificationPositions.panel: {
          action = Actions.Notification.setNotification(
            'notification',
            value,
            ...rest
          );
          break;
        }
        case NotificationPositions.transfer: {
          action = Actions.Notification.pushTransfer({
            align: `.${BASE_CLASS_NAME}`,
            text: value,
            ...rest,
          });
          break;
        }
        case NotificationPositions.none:
        default:
          return;
      }
      dispatch(action);
    },
    clearNotification: () => {
      let action;
      switch (notificationPosition) {
        case NotificationPositions.panel: {
          action = Actions.Notification.resetNotification();
          break;
        }
        case NotificationPositions.transfer: {
          action = Actions.Notification.clearTransfer();
          break;
        }
        case NotificationPositions.none:
        default:
          return;
      }
      dispatch(action);
    },
  };
};

export { TransferLink };
export default connect(mapStateToProps, mapDispatchToProps, null, {
  forwardRef: true,
})(TransferLink);
